<template>
  <div>
    <div
      class="xpage-search-filters"
      @click.self="localApplications.filterDisplay = false"
      :class="{ visible: localApplications.filterDisplay }"
    >
      <div class="xpage-search-filter">
        <div
          class="xpage-search-filters-close icon-x"
          @click="
            localApplications.filterDisplay = false;
            $emit('closeFilters');
          "
        ></div>
        <h3>Filters</h3>
        <div class="xpage-search-filter-scroll">
          <div
            class="xpage-search-filter-section"
            v-for="(section, index) in filteredSections"
            :key="index"
          >
            <h3 class="section-description">{{ section.description }}</h3>

            <!-- Render the Annual Review Year Dropdown -->
            <div
              v-if="
                section.param === FILTER_ANNUAL_REVIEW_STATUS &&
                annualReviewYearFilter
              "
            >
              <label>
                <h4>Annual Review Year:</h4>
                <select
                  @change="handleSelectChange($event, annualReviewYearFilter)"
                >
                  <option
                    v-for="(item, itemIndex) in annualReviewYearFilter.results"
                    :key="itemIndex"
                    :value="item.id"
                    :selected="item.checked"
                  >
                    {{ item.name }}
                  </option>
                </select>
              </label>
              <div v-if="annualReviewErrorMessage" class="error">
                {{ annualReviewErrorMessage }}
              </div>
            </div>

            <div v-if="sectionResultsExceedMaxVisible(section)">
              <input
                type="text"
                v-model.trim="section.search"
                :placeholder="`Filter ${section.description}...`"
              />
            </div>
            <div
              v-for="(item, itemIndex) in filteredSectionResults(section).slice(
                0,
                maxFilterResults
              )"
              :key="itemIndex"
            >
              <div v-if="section.type == 'radio'">
                <label class="xradio">
                  {{ item.name }}
                  <input
                    type="radio"
                    :name="'radio' + section.param"
                    :checked="item.checked"
                    v-on:input="selectFilter($event, item, section)"
                  />
                  <span></span>
                </label>
              </div>
              <div v-else>
                <label
                  class="xcheck"
                  :title="item?.displayName ? item?.displayName : undefined"
                >
                  {{ item.name }}
                  <input
                    type="checkbox"
                    :checked="item.checked"
                    v-on:input="selectFilter($event, item)"
                  />
                  <span></span>
                </label>
                <div v-if="item.children" style="padding-left: 20px">
                  <label
                    v-for="(child, childIndex) in item.children"
                    :key="childIndex"
                    class="xcheck"
                    :title="child?.displayName ? child?.displayName : undefined"
                  >
                    {{ child.name }}
                    <input
                      type="checkbox"
                      :checked="child.checked"
                      v-on:input="selectFilter($event, child, item)"
                    />
                    <span></span>
                  </label>
                </div>
              </div>
            </div>

            <div
              v-if="sectionResultsExceedMaxVisible(section)"
              class="xseeall"
              @click="localApplications.modal = index"
            >
              See all
            </div>
            <div
              class="xmodal-wrapper"
              v-if="localApplications.modal === index"
            >
              <div class="xmodal-inner">
                <div class="xmodal">
                  <div
                    class="xmodal-close"
                    @click="localApplications.modal = false"
                  >
                    X
                  </div>
                  <h2>{{ section.description }}</h2>
                  <input
                    type="text"
                    v-model.trim="section.search"
                    :placeholder="`Filter ${section.description}...`"
                  />
                  <div
                    style="
                      text-align: left;
                      max-height: 300px;
                      overflow-y: auto;
                    "
                  >
                    <div
                      v-for="(item, itemIndex) in filteredSectionResults(
                        section
                      )"
                      :key="itemIndex"
                    >
                      <label class="xcheck">
                        {{ item.name }}
                        <input
                          type="checkbox"
                          :checked="item.checked"
                          v-on:input="selectFilter($event, item)"
                        />
                        <span></span>
                      </label>
                    </div>
                  </div>
                  <div class="xmodal-buttons">
                    <button @click="localApplications.modal = false">
                      Show {{ localApplications.query.totalCount }} Result<span
                        v-if="localApplications.query.totalCount != 1"
                        >s</span
                      >
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div
        class="xpage-search-filter-confirm"
        @click="localApplications.filterDisplay = false"
      >
        Show {{ applications.query.totalCount }} Result<span
          v-if="applications.query.totalCount != 1"
          >s</span
        >
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, PropType, watch, computed } from "vue";

const props = defineProps({
  applications: {
    type: Object as PropType<any>,
    required: true,
  },
  triggerSearch: {
    type: Function,
    required: true,
  },
  pageNumber: {
    type: Number,
    required: false,
  },
  pageTotal: {
    type: Number,
    required: true,
  },
  maxFilterResults: {
    type: Number,
    required: false,
    default: 5,
  },
  sectionResultsExceedMaxVisible: {
    type: Function,
    required: false,
    default: (section: any) => {
      return section.results.length > 5;
    },
  },
  filteredSectionResults: {
    type: Function,
    required: false,
    default: (section: any) => {
      return section.results;
    },
  },
  selectFilter: {
    type: Function,
    required: false,
    default: (event: any, item: any, parent: any) => {
      if (parent) {
        parent.checked = parent.children.every((child: any) => child.checked);
      }
    },
  },
  annualReviewErrorMessage: {
    type: String,
    required: false,
    default: "",
  },
});

let localApplications = ref(JSON.parse(JSON.stringify(props.applications)));

const FILTER_ANNUAL_REVIEW_STATUS = "AnnualReviewStatusIds";
const FILTER_ANNUAL_REVIEW_YEAR = "AnnualReviewYear";

const annualReviewYearFilter = computed(() => {
  return props.applications.filters.find(
    (f) => f.param === FILTER_ANNUAL_REVIEW_YEAR
  );
});

const filteredSections = computed(() => {
  if (!annualReviewYearFilter.value) {
    return props.applications.filters.filter(
      (f) =>
        f.param !== FILTER_ANNUAL_REVIEW_YEAR &&
        f.param !== FILTER_ANNUAL_REVIEW_STATUS
    );
  }
  return props.applications.filters.filter(
    (f) => f.param !== FILTER_ANNUAL_REVIEW_YEAR
  );
});

const handleSelectChange = (event, filterSingle) => {
  if (!event.target) return;
  const selectedValue = event.target.value;
  const selectedItem =
    filterSingle.results.find((item) => item.id == selectedValue) || null;
  props.selectFilter(event, selectedItem, filterSingle);
};

watch(
  () => props.applications.query,
  (newQuery) => {
    localApplications.value.query.totalCount = newQuery.totalCount;
  },
  { deep: true }
);

watch(
  () => props.applications.filterDisplay,
  (newFilterDisplay) => {
    localApplications.value.filterDisplay = newFilterDisplay;
  }
);
</script>

<style scoped>
.section-description {
  margin-bottom: 10px;
}
.error {
  color: #e31838;
  font-style: italic;
  font-size: 12px;
}
</style>
